import React, { useCallback, useMemo, useState } from "react";
import { Package, Search } from "lucide-react";

import Topbar from "../components/Topbar";
import { Drawer, Field, Section } from "../components/ui/Drawer";
import {
  EmptyState,
  ErrorState,
  TableSkeleton,
  useToast,
} from "../components/ui/Feedback";
import { FilterTabs, Pagination, useDebounced } from "../components/ui/Table";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { setOrderStatus } from "../lib/api";
import { ORDER_STATUSES, type OrderStatus } from "../lib/types";

/**
 * Orders.
 *
 * Three fixes over the previous version, in order of how much they cost you:
 *
 *   1. THE STATUS LIST WAS WRONG. This page offered pending / processing /
 *      completed / cancelled. The app renders pending / processing / shipped /
 *      delivered / cancelled and falls back to pending's amber styling for
 *      anything it doesn't recognise — so "completed" showed customers an
 *      amber pending badge, and *shipped* and *delivered*, the two states
 *      someone waiting on a parcel actually wants to see, could not be set at
 *      all.
 *
 *   2. It fetched every order ever placed, unpaginated, and filtered in the
 *      browser.
 *
 *   3. It never showed what was in an order. There was no way to see line
 *      items or a delivery address from this panel, which makes it useless for
 *      the thing you actually open it for — answering "what did they buy and
 *      where is it going".
 */

const PAGE_SIZE = 40;

const STATUS_TABS: { key: OrderStatus | "all"; label: string; tone?: string }[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending", tone: "#d99e00" },
  { key: "processing", label: "Processing", tone: "#6c5ffc" },
  { key: "shipped", label: "Shipped", tone: "#4ec2f0" },
  { key: "delivered", label: "Delivered", tone: "#19b159" },
  { key: "cancelled", label: "Cancelled", tone: "var(--danger)" },
];

const BADGE: Record<string, string> = {
  pending: "badge badge-warning",
  processing: "badge badge-primary",
  shipped: "badge",
  delivered: "badge badge-success",
  cancelled: "badge badge-danger",
};

const money = (amount: number | string | null, currency?: string | null) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: (currency || "GBP").toUpperCase(),
  }).format(numeric);
};

const dateTime = (value: string | null) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

type OrderRow = {
  id: string;
  user_id: string | null;
  status: string;
  total_amount: number | null;
  currency: string | null;
  created_at: string;
  refunded_at: string | null;
  refunded_amount: number | null;
  delivery_address: Record<string, string> | null;
  profiles: { full_name: string | null } | null;
};

async function fetchOrders(params: {
  search: string;
  status: OrderStatus | "all";
  offset: number;
}): Promise<{ rows: OrderRow[]; total: number }> {
  // `email` is not joined: migration 0010 revokes it at the column level, and
  // asking for it would fail the entire query rather than omit the field. The
  // address on the order carries a contact name anyway, which is the one that
  // matters for fulfilment.
  let query = supabase
    .from("orders")
    .select(
      "id, user_id, status, total_amount, currency, created_at, refunded_at, refunded_amount, delivery_address, profiles(full_name)",
      { count: "exact" }
    )
    .order("created_at", { ascending: false })
    .range(params.offset, params.offset + PAGE_SIZE - 1);

  if (params.status !== "all") query = query.eq("status", params.status);

  // Order ids are uuids, so a partial id search only makes sense as a prefix.
  // Names live on the joined profile and on the delivery address, and
  // PostgREST can't OR across an embedded resource — so the id is what this
  // box searches, and it says so in the placeholder.
  const term = params.search.trim();
  if (term) query = query.ilike("id", `${term.replace(/[%,()]/g, "")}%`);

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as unknown as OrderRow[], total: count ?? 0 };
}

async function fetchOrderItems(orderId: string) {
  const { data, error } = await supabase
    .from("order_items")
    .select("id, product_name, product_brand, product_image, quantity, unit_price, total_price")
    .eq("order_id", orderId);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export default function Orders() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<OrderStatus | "all">("all");
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<OrderRow | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const debounced = useDebounced(search);

  const list = useQuery(
    () => fetchOrders({ search: debounced, status, offset }),
    [debounced, status, offset]
  );

  const items = useQuery(
    () => (open ? fetchOrderItems(open.id) : Promise.resolve([])),
    [open?.id]
  );

  // Memoised because the revenue sum below depends on it, and `?? []` builds a
  // new array identity on every render otherwise.
  const rows = useMemo(() => list.data?.rows ?? [], [list.data]);

  const changeStatus = useCallback(
    async (order: OrderRow, next: OrderStatus) => {
      setSaving(order.id);
      try {
        await setOrderStatus(order.id, next);
        toast.success(`Order marked ${next}`);
        list.refetch();
        setOpen((current) =>
          current && current.id === order.id ? { ...current, status: next } : current
        );
      } catch (err) {
        // The select snaps back on refetch, so the row never shows a status
        // the database didn't accept.
        toast.failure(err);
        list.refetch();
      } finally {
        setSaving(null);
      }
    },
    [toast, list]
  );

  const revenue = useMemo(
    () =>
      rows
        .filter((order) => order.status !== "cancelled")
        .reduce((sum, order) => sum + Number(order.total_amount ?? 0), 0),
    [rows]
  );

  return (
    <div>
      <Topbar
        title="Orders"
        subtitle={
          list.data
            ? `${list.data.total.toLocaleString()} orders · ${money(revenue, rows[0]?.currency)} on this page`
            : undefined
        }
      />
      <div className="page-shell">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            marginBottom: 18,
            flexWrap: "wrap",
          }}
        >
          <FilterTabs
            tabs={STATUS_TABS}
            active={status}
            onChange={(next) => {
              setStatus(next);
              setOffset(0);
            }}
          />
          <div style={{ position: "relative", minWidth: 240, flex: "0 1 300px" }}>
            <Search
              size={14}
              color="var(--text-muted)"
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              className="input"
              placeholder="Order ID starts with…"
              style={{ paddingLeft: 34 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
            />
          </div>
        </div>

        <div className="card">
          {list.error ? (
            <ErrorState error={list.error} onRetry={list.refetch} />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Placed</th>
                      <th>Status</th>
                      <th>Change to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading && <TableSkeleton rows={8} cols={6} />}

                    {!list.loading &&
                      rows.map((order) => (
                        <tr key={order.id} onClick={() => setOpen(order)} style={{ cursor: "pointer" }}>
                          <td style={{ fontWeight: 700, color: "var(--primary)", fontFamily: "ui-monospace, Menlo, monospace" }}>
                            #{order.id.slice(0, 8).toUpperCase()}
                          </td>
                          <td>
                            <div style={{ fontWeight: 650 }}>
                              {order.profiles?.full_name ??
                                order.delivery_address?.full_name ??
                                "—"}
                            </div>
                            {order.delivery_address?.city && (
                              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                {order.delivery_address.city}
                              </div>
                            )}
                          </td>
                          <td style={{ fontWeight: 750 }}>
                            {money(order.total_amount, order.currency)}
                            {order.refunded_at && (
                              <div style={{ fontSize: 11, color: "var(--danger)", fontWeight: 700 }}>
                                refunded {money(order.refunded_amount, order.currency)}
                              </div>
                            )}
                          </td>
                          <td>{dateTime(order.created_at)}</td>
                          <td>
                            <span className={BADGE[order.status] ?? "badge"}>{order.status}</span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              value={order.status}
                              disabled={saving === order.id}
                              onChange={(e) => changeStatus(order, e.target.value as OrderStatus)}
                              className="input"
                              style={{ minHeight: 34, padding: "6px 10px", fontSize: 12.5, width: "auto" }}
                            >
                              {ORDER_STATUSES.map((value) => (
                                <option key={value} value={value}>
                                  {value[0].toUpperCase() + value.slice(1)}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}

                    {!list.loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <EmptyState message="No orders in this view" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                offset={offset}
                limit={PAGE_SIZE}
                total={list.data?.total ?? 0}
                onChange={setOffset}
                busy={list.fetching}
              />
            </>
          )}
        </div>
      </div>

      <Drawer
        open={!!open}
        onClose={() => setOpen(null)}
        title={open ? `#${open.id.slice(0, 8).toUpperCase()}` : "Order"}
        subtitle={open ? dateTime(open.created_at) : undefined}
        width={560}
      >
        {open && (
          <>
            <Section title="Summary">
              <Field
                label="Status"
                value={<span className={BADGE[open.status] ?? "badge"}>{open.status}</span>}
              />
              <Field label="Total" value={money(open.total_amount, open.currency)} />
              {open.refunded_at && (
                <Field
                  label="Refunded"
                  value={`${money(open.refunded_amount, open.currency)} on ${dateTime(open.refunded_at)}`}
                />
              )}
              <Field label="Order ID" value={open.id} mono />
              <Field label="Customer ID" value={open.user_id} mono />
            </Section>

            <Section title="Delivery address">
              {open.delivery_address ? (
                Object.entries(open.delivery_address)
                  .filter(([, value]) => !!value)
                  .map(([key, value]) => (
                    <Field key={key} label={key.replace(/_/g, " ")} value={value} />
                  ))
              ) : (
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  No address recorded
                </div>
              )}
            </Section>

            <Section title="Items">
              {items.error && <ErrorState error={items.error} onRetry={items.refetch} />}
              {items.loading && (
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Loading…</div>
              )}
              {items.data?.length === 0 && !items.loading && (
                <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                  No line items recorded for this order
                </div>
              )}
              {items.data?.map((item: any) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  {item.product_image ? (
                    <img
                      src={item.product_image}
                      alt=""
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        objectFit: "cover",
                        border: "1px solid var(--border)",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        background: "var(--bg)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Package size={16} color="var(--text-muted)" />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{item.product_name}</div>
                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                      {item.product_brand} · {item.quantity} ×{" "}
                      {money(item.unit_price, open.currency)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 750, fontSize: 13 }}>
                    {money(item.total_price, open.currency)}
                  </div>
                </div>
              ))}
            </Section>
          </>
        )}
      </Drawer>
    </div>
  );
}
